/**
 * v8.9.10 (Feb 2026) CONTRACT-ARCH FIX diagnostic widget — OPERATOR-ONLY.
 *
 * Surfaces the last 20 Deriv proposal/buy rejection events captured by
 * bot_worker._place_trade. Answers "why aren't fires becoming trades?"
 * at a glance — each row shows the symbol, contract_type, strategy +
 * multiplier the bot tried, and the raw Deriv error message.
 *
 * Lives at /operator only — wired in via CloudBotPanel `operatorMode`.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const kindBadge = (kind) =>
  kind === "buy"
    ? "bg-orange-500/15 text-orange-200 border-orange-500/40"
    : "bg-rose-500/15 text-rose-200 border-rose-500/40";

export const RecentRejectionsWidget = ({ accountId }) => {
  const [data, setData] = useState({ rejections: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/bot/recent-rejections/${accountId}`);
      setData(res.data || { rejections: [], count: 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [load]);

  const rows = data.rejections || [];

  return (
    <div
      data-testid="recent-rejections-widget"
      className="mt-4 border border-vts-line bg-vts-bg/40 rounded-md p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
          Deriv rejections · last {data.count}/20
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="recent-rejections-refresh"
          className="text-[10px] font-mono uppercase text-vts-silver/60 hover:text-white transition disabled:opacity-50"
        >
          {loading ? "…" : "↻ refresh"}
        </button>
      </div>

      {err && (
        <div className="text-[11px] text-rose-300 font-mono mb-2">{err}</div>
      )}

      {rows.length === 0 ? (
        <div className="text-[11px] text-vts-silver/50 font-mono py-2">
          No Deriv rejections recorded since backend start. ✅
          <br />
          <span className="text-vts-silver/35">
            (If fires → 0 trades, this list will populate with the raw error.)
          </span>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {rows
            .slice()
            .reverse()
            .map((r, i) => (
              <div
                key={`${r.ts}-${i}`}
                data-testid={`recent-rejection-row-${i}`}
                className="text-[11px] font-mono bg-vts-bg/60 border border-vts-line/60 rounded px-2 py-1.5"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-vts-silver/45 shrink-0">
                    {fmtTime(r.ts)}
                  </span>
                  <span
                    className={`inline-block px-1.5 py-0 rounded-sm border text-[9px] uppercase tracking-wider ${kindBadge(
                      r.kind
                    )}`}
                  >
                    {r.kind || "?"}
                  </span>
                  <span className="text-white font-semibold">{r.symbol}</span>
                  <span className="text-vts-silver/70">
                    {r.contract_type}
                  </span>
                  <span className="text-vts-silver/45">
                    {r.strategy}
                    {r.multiplier ? ` ×${r.multiplier}` : ""}
                  </span>
                  {typeof r.stake === "number" && (
                    <span className="text-vts-silver/45">${r.stake}</span>
                  )}
                </div>
                <div className="mt-0.5 text-rose-200/90 whitespace-pre-wrap break-words">
                  {r.error || "(no message)"}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default RecentRejectionsWidget;
